import React, {
  Fragment,
  Suspense,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { useQueryClient } from "@tanstack/react-query";

import MedicalHistoryScreen from "./MedicalHistoryScreen";
import {
  MedicalHistoryThemeProvider,
  MOON_THEME,
} from "@src/packages/medical-history/theme/MedicalHistoryTheme";
import { MedicalHistoryQuestionProvider } from "@src/packages/medical-history/components";

import { BasicInfoProvider } from "@src/packages/medical-history/basic-info/BasicInfoContext";
import { SexAtBirthScreen } from "@src/packages/medical-history/basic-info/SexAtBirthScreen/SexAtBirthScreen";
import { BasicInfoScreen } from "@src/packages/medical-history/basic-info/BasicInfoScreen/BasicInfoScreen";
import { QuickExposureScreen } from "@src/packages/medical-history/basic-info/QuickExposureScreen/QuickExposureScreen";
import { PregnancyScreen as BasicPregnancyScreen } from "@src/packages/medical-history/basic-info/PregnancyScreen/PregnancyScreen";
import { EndometriosisScreen } from "@src/packages/medical-history/basic-info/EndometriosisScreen/EndometriosisScreen";
import { PCOSScreen } from "@src/packages/medical-history/basic-info/PCOSScreen/PCOSScreen";
import { VaricoceleScreen } from "@src/packages/medical-history/basic-info/VaricoceleScreen/VaricoceleScreen";
import { CancerScreen as BasicCancerScreen } from "@src/packages/medical-history/basic-info/CancerScreen/CancerScreen";
import { AllergiesScreen as BasicAllergiesScreen } from "@src/packages/medical-history/basic-info/AllergiesScreen/AllergiesScreen";
import { AllergiesDetailsScreen as BasicAllergiesDetailsScreen } from "@src/packages/medical-history/basic-info/AllergiesDetailsScreen/AllergiesDetailsScreen";
import { MedicationsScreen as BasicMedicationsScreen } from "@src/packages/medical-history/basic-info/MedicationsScreen/MedicationsScreen";

import { InfertilityProvider } from "@src/packages/medical-history/fertility/infertility/InfertilityContext";
import { InfertilityDiagnosisScreen } from "@src/packages/medical-history/fertility/infertility/InfertilityDiagnosisScreen/InfertilityDiagnosisScreen";
import { InfertilityCauseScreen } from "@src/packages/medical-history/fertility/infertility/InfertilityCauseScreen/InfertilityCauseScreen";
import { InfertilityCauseOtherScreen } from "@src/packages/medical-history/fertility/infertility/InfertilityCauseOtherScreen/InfertilityCauseOtherScreen";
import { PartnerInfertilityDiagnosisScreen } from "@src/packages/medical-history/fertility/infertility/PartnerInfertilityDiagnosisScreen/PartnerInfertilityDiagnosisScreen";

import { FertilityTestingProvider } from "@src/packages/medical-history/fertility/fertility-testing/FertilityTestingContext";
import { FertilityTestingTypesScreen } from "@src/packages/medical-history/fertility/fertility-testing/FertilityTestingTypesScreen/FertilityTestingTypesScreen";
import { UrineTestsScreen } from "@src/packages/medical-history/fertility/fertility-testing/UrineTestsScreen/UrineTestsScreen";
import { ImagingTestsScreen } from "@src/packages/medical-history/fertility/fertility-testing/ImagingTestsScreen/ImagingTestsScreen";
import { SamplingTestsScreen } from "@src/packages/medical-history/fertility/fertility-testing/SamplingTestsScreen/SamplingTestsScreen";
import { GeneticTestsScreen } from "@src/packages/medical-history/fertility/fertility-testing/GeneticTestsScreen/GeneticTestsScreen";
import { BloodTestsScreen } from "@src/packages/medical-history/fertility/fertility-testing/BloodTestsScreen/BloodTestsScreen";
import { FertilityTestingCompleteScreen } from "@src/packages/medical-history/fertility/fertility-testing/FertilityTestingCompleteScreen/FertilityTestingCompleteScreen";

import { FertilityTreatmentsProvider } from "@src/packages/medical-history/fertility/fertility-treatments/FertilityTreatmentsContext";
import { FertilityTreatmentsTypesScreen } from "@src/packages/medical-history/fertility/fertility-treatments/FertilityTreatmentsTypesScreen/FertilityTreatmentsTypesScreen";
import { IUITreatmentsScreen } from "@src/packages/medical-history/fertility/fertility-treatments/IUITreatmentsScreen/IUITreatmentsScreen";
import { IVFTreatmentsScreen } from "@src/packages/medical-history/fertility/fertility-treatments/IVFTreatmentsScreen/IVFTreatmentsScreen";
import { MedsOnlyTreatmentsScreen } from "@src/packages/medical-history/fertility/fertility-treatments/MedsOnlyTreatmentsScreen/MedsOnlyTreatmentsScreen";
import { OtherTreatmentsScreen } from "@src/packages/medical-history/fertility/fertility-treatments/OtherTreatmentsScreen/OtherTreatmentsScreen";
import { FETTreatmentsScreen } from "@src/packages/medical-history/fertility/fertility-treatments/FETTreatmentsScreen/FETTreatmentsScreen";
import { EggFreezingScreen } from "@src/packages/medical-history/fertility/fertility-treatments/EggFreezingScreen/EggFreezingScreen";
import { EmbryoFreezingScreen } from "@src/packages/medical-history/fertility/fertility-treatments/EmbryoFreezingScreen/EmbryoFreezingScreen";

import { MenstrualHealthProvider } from "@src/packages/medical-history/gynae/menstrual-health/MenstrualHealthContext";
import { CycleStatusScreen } from "@src/packages/medical-history/gynae/menstrual-health/screens/CycleStatusScreen";
import { AgeAtFirstPeriodScreen } from "@src/packages/medical-history/gynae/menstrual-health/screens/AgeAtFirstPeriodScreen";
import { Last3PeriodsScreen } from "@src/packages/medical-history/gynae/menstrual-health/screens/Last3PeriodsScreen";
import { BleedOutsidePeriodScreen } from "@src/packages/medical-history/gynae/menstrual-health/screens/BleedOutsidePeriodScreen";
import { PainScaleScreen } from "@src/packages/medical-history/gynae/menstrual-health/screens/PainScaleScreen";
import { PainMedicationScreen } from "@src/packages/medical-history/gynae/menstrual-health/screens/PainMedicationScreen";
import { SymptomsScreen } from "@src/packages/medical-history/gynae/menstrual-health/screens/SymptomsScreen";
import { FertileWindowScreen } from "@src/packages/medical-history/gynae/menstrual-health/screens/FertileWindowScreen";

import { SexualHealthProvider } from "@src/packages/medical-history/gynae/sexual-health/SexualHealthContext";
import { PapSmearScreen } from "@src/packages/medical-history/gynae/sexual-health/screens/PapSmearScreen";
import { PapSmearProceduresScreen } from "@src/packages/medical-history/gynae/sexual-health/screens/PapSmearProceduresScreen";
import { PapSmearProcedureDetailsScreen } from "@src/packages/medical-history/gynae/sexual-health/screens/PapSmearProcedureDetailsScreen";
import { STDsScreen } from "@src/packages/medical-history/gynae/sexual-health/screens/STDsScreen";
import { STDDetailsScreen } from "@src/packages/medical-history/gynae/sexual-health/screens/STDDetailsScreen";
import { VaginalInfectionsScreen } from "@src/packages/medical-history/gynae/sexual-health/screens/VaginalInfectionsScreen";
import { VaginalInfectionDetailsScreen } from "@src/packages/medical-history/gynae/sexual-health/screens/VaginalInfectionDetailsScreen";
import { PainfulIntercourseScreen } from "@src/packages/medical-history/gynae/sexual-health/screens/PainfulIntercourseScreen";
import { LubricantsScreen } from "@src/packages/medical-history/gynae/sexual-health/screens/LubricantsScreen";
import { PartnerEjaculateScreen } from "@src/packages/medical-history/gynae/sexual-health/screens/PartnerEjaculateScreen";
import { ErectileDifficultiesScreen } from "@src/packages/medical-history/gynae/sexual-health/screens/ErectileDifficultiesScreen";

import { ContraceptionProvider } from "@src/packages/medical-history/gynae/contraception/ContraceptionContext";
import { ContraceptionMethodsScreen } from "@src/packages/medical-history/gynae/contraception/screens/ContraceptionMethodsScreen";
import { ContraceptionDetailsScreen } from "@src/packages/medical-history/gynae/contraception/screens/ContraceptionDetailsScreen";

import { PregnancyProvider } from "@src/packages/medical-history/gynae/pregnancies/PregnancyContext";
import { PregnancyUsageScreen } from "@src/packages/medical-history/gynae/pregnancies/screens/PregnancyUsageScreen";
import { PregnancyDetailsScreen } from "@src/packages/medical-history/gynae/pregnancies/screens/PregnancyDetailsScreen";

import { MedicationsVaccinesProvider } from "@src/packages/medical-history/medical/medications-vaccines/MedicationsVaccinesContext";
import { MedicationsIntroScreen } from "@src/packages/medical-history/medical/medications-vaccines/MedicationsIntroScreen/MedicationsIntroScreen";
import { MedicationsProductsScreen } from "@src/packages/medical-history/medical/medications-vaccines/MedicationsProductsScreen/MedicationsProductsScreen";
import { MedicationDetailsScreen } from "@src/packages/medical-history/medical/medications-vaccines/MedicationDetailsScreen/MedicationDetailsScreen";
import { VaccinesSelectScreen } from "@src/packages/medical-history/medical/medications-vaccines/VaccinesSelectScreen/VaccinesSelectScreen";
import { VaccineDetailsScreen } from "@src/packages/medical-history/medical/medications-vaccines/VaccineDetailsScreen/VaccineDetailsScreen";

import { AllergiesProvider } from "@src/packages/medical-history/medical/allergies/AllergiesContext";
import { AllergiesIntroScreen } from "@src/packages/medical-history/medical/allergies/AllergiesIntroScreen/AllergiesIntroScreen";
import { AllergiesDetailsScreen } from "@src/packages/medical-history/medical/allergies/AllergiesDetailsScreen/AllergiesDetailsScreen";

import { SurgeriesProvider } from "@src/packages/medical-history/medical/surgeries/SurgeriesContext";
import { SurgeriesScreen } from "@src/packages/medical-history/medical/surgeries/screens/SurgeriesScreen";
import { SurgeriesDetailsScreen } from "@src/packages/medical-history/medical/surgeries/screens/SurgeriesDetailsScreen";

import { DiseaseHistoryProvider } from "@src/packages/medical-history/medical/disease-history/DiseaseHistoryContext";
import { BreastTestingScreen } from "@src/packages/medical-history/medical/disease-history/breast-testing/screens/BreastTestingScreen";
import { BreastTestingDetailsScreen } from "@src/packages/medical-history/medical/disease-history/breast-testing/screens/BreastTestingDetailsScreen";
import { BreastConditionsScreen } from "@src/packages/medical-history/medical/disease-history/breast-testing/screens/BreastConditionsScreen";
import { BreastSymptomOtherScreen } from "@src/packages/medical-history/medical/disease-history/breast-testing/screens/BreastSymptomOtherScreen";
import { DiseaseSelectionScreen } from "@src/packages/medical-history/medical/disease-history/diseases/screens/DiseaseSelectionScreen";
import { DiseaseDetailsScreen } from "@src/packages/medical-history/medical/disease-history/diseases/screens/DiseaseDetailsScreen";
import { CancerHistoryScreen } from "@src/packages/medical-history/medical/disease-history/cancer/screens/CancerHistoryScreen";
import { CancerDetailsScreen } from "@src/packages/medical-history/medical/disease-history/cancer/screens/CancerDetailsScreen";
import { CancerTreatmentsScreen } from "@src/packages/medical-history/medical/disease-history/cancer/screens/CancerTreatmentsScreen";
import { CancerTreatmentDetailsScreen } from "@src/packages/medical-history/medical/disease-history/cancer/screens/CancerTreatmentDetailsScreen";
import { DISEASE_CATEGORY_OPTIONS } from "@src/packages/medical-history/medical/disease-history/diseases/options";

import { ExposureProvider } from "@src/packages/medical-history/lifestyle/exposure/ExposureContext";
import { ExposureTypesScreen } from "@src/packages/medical-history/lifestyle/exposure/screens/ExposureTypesScreen";
import { ExposureDetailsScreen } from "@src/packages/medical-history/lifestyle/exposure/screens/ExposureDetailsScreen";

import { SubstancesProvider } from "@src/packages/medical-history/lifestyle/substances/SubstancesContext";
import { SubstancesIntroScreen } from "@src/packages/medical-history/lifestyle/substances/screens/SubstancesIntroScreen";
import { SubstancesSelectScreen } from "@src/packages/medical-history/lifestyle/substances/screens/SubstancesSelectScreen";
import { SubstanceDetailsScreen } from "@src/packages/medical-history/lifestyle/substances/screens/SubstanceDetailsScreen";

import { SleepActivityProvider } from "@src/packages/medical-history/lifestyle/sleep-activity/SleepActivityContext";
import { SleepScreen } from "@src/packages/medical-history/lifestyle/sleep-activity/screens/SleepScreen";
import { ActivityScreen } from "@src/packages/medical-history/lifestyle/sleep-activity/screens/ActivityScreen";

import { DietSupplementsProvider } from "@src/packages/medical-history/lifestyle/diet-supplements/DietSupplementsContext";
import { DietScreen } from "@src/packages/medical-history/lifestyle/diet-supplements/screens/DietScreen";
import { SupplementsScreen } from "@src/packages/medical-history/lifestyle/diet-supplements/screens/SupplementsScreen";
import { SupplementsDetailsScreen } from "@src/packages/medical-history/lifestyle/diet-supplements/screens/SupplementsDetailsScreen";

import { FamilyHistoryProvider } from "@src/packages/medical-history/family-history/FamilyHistoryContext";
import { EthnicityScreen } from "@src/packages/medical-history/family-history/screens/EthnicityScreen";
import { DnaTestScreen } from "@src/packages/medical-history/family-history/screens/DnaTestScreen";
import { DnaTestReportScreen } from "@src/packages/medical-history/family-history/screens/DnaTestReportScreen";
import { AdoptedScreen } from "@src/packages/medical-history/family-history/screens/AdoptedScreen";
import { FamilyInfertilityScreen } from "@src/packages/medical-history/family-history/screens/FamilyInfertilityScreen";
import { FamilyMemberScreen } from "@src/packages/medical-history/family-history/screens/FamilyMemberScreen";
import { DesScreen } from "@src/packages/medical-history/family-history/screens/DesScreen";
import { MaternalDiseasesScreen } from "@src/packages/medical-history/family-history/screens/MaternalDiseasesScreen";
import { PaternalDiseasesScreen } from "@src/packages/medical-history/family-history/screens/PaternalDiseasesScreen";
import { SEXUAL_HEALTH_QUESTION_APPLICABILITY } from "@src/packages/medical-history/gynae/sexual-health/sexualHealthApplicability";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type RuntimeRouteParams = Record<string, object | undefined>;
type RuntimeProvider = ComponentType<{ children: ReactNode }>;
type RuntimeScreen = {
  name: string;
  label: string;
  component: ComponentType;
  initialParams?: object;
  patientSex?: "AFAB" | "AMAB";
  applicability?: {
    condition: string;
    wording?: string;
  };
};
type RuntimeFlow = {
  id: string;
  category: string;
  provider?: RuntimeProvider;
  suspense?: boolean;
  screens: RuntimeScreen[];
};
type RuntimeTarget = RuntimeScreen & {
  id: string;
  category: string;
  flow: RuntimeFlow;
};

type RuntimePatientFixture = {
  id: string;
  gender?: "AFAB" | "AMAB";
  [key: string]: unknown;
};

type RuntimeMedicalHistoryFixture = {
  diseases?: { disease?: string }[];
  [key: string]: unknown;
};

const RuntimeStack = createNativeStackNavigator<RuntimeRouteParams>();

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function MedicalHistoryOverviewSurface() {
  type Props = React.ComponentProps<typeof MedicalHistoryScreen>;
  const navigation = useNavigation() as Props["navigation"];
  const route = {
    key: "MedicalHistory",
    name: "MedicalHistory",
  } as Props["route"];

  return <MedicalHistoryScreen navigation={navigation} route={route} />;
}

const MOCK_PATIENT_AFAB: RuntimePatientFixture = {
  id: "medical-history-canvas-patient",
  firstName: "Avery",
  lastName: "Canvas",
  gender: "AFAB",
  dateOfBirth: "1991-04-12",
  bodyData: {
    id: "body-canvas",
    height: 168,
    weight: 62,
  },
  testResults: [
    {
      id: "test-blood",
      type: "blood",
      bloodTestCategory: "hormonal",
      name: "AMH",
      date: "2026-01-11",
      patientValue: "2.4",
      unit: "ng/mL",
      notes: "Drawn on cycle day 3.",
    },
    {
      id: "test-urine",
      type: "urine",
      urineTestCategory: "ovulation_kit",
      name: "LH home strips",
      date: "2026-01-18",
      patientValue: "positive",
    },
    {
      id: "test-genetic",
      type: "genetics",
      geneticsTestCategory: "carrier_testing",
      name: "Expanded carrier screen",
      date: "2025-11-03",
      notes: "Partner testing pending.",
    },
    {
      id: "test-imaging",
      type: "imaging",
      imagingTestCategory: "antral_follicle_count",
      name: "AFC ultrasound",
      date: "2026-02-07",
      patientValue: "18 follicles",
    },
    {
      id: "test-sampling",
      type: "sampling",
      samplingTestCategory: "pap_smear",
      name: "Pap smear",
      date: "2025-06-19",
      patientValue: "normal",
    },
    {
      id: "test-breast",
      type: "imaging",
      imagingTestCategory: "mammogram",
      name: "Screening mammogram",
      date: "2025-08-14",
      patientValue: "BIRADS 1",
      notes: "Routine screening.",
    },
  ],
  fertilityHistory: {
    id: "fertility-history-canvas",
    hasPriorFertilityTreatments: true,
    infertilityHistory: {
      id: "infertility-history-canvas",
      isDiagnosedWithInfertility: true,
      isARTNeededForCarrierDisease: false,
      isPartnerInfertile: true,
      partnerInfertilityCause: "low sperm count",
      notes: "Partner saw a reproductive urologist in 2025.",
      causes: [
        {
          id: "inf-cause-1",
          cause: "endometriosis",
        },
        {
          id: "inf-cause-2",
          cause: "other",
          other: "Possible luteal phase issue.",
        },
      ],
    },
  },
  cycles: [
    {
      id: "cycle-iui",
      cycleType: "iui",
      status: "closed",
      description: "IUI with letrozole",
      startDate: "2024-06-04",
      clinic: "Louise Fertility",
      outcome: { type: "no_pregnancy", notes: "Negative beta." },
    },
    {
      id: "cycle-ivf",
      cycleType: "ivf",
      status: "closed",
      description: "IVF antagonist cycle",
      startDate: "2025-02-09",
      clinic: "Louise Fertility",
      outcome: { type: "other", notes: "Three blastocysts frozen." },
      dayNotes: [{ notes: "Mild OHSS precautions." }],
    },
    {
      id: "cycle-fet",
      cycleType: "fet",
      status: "closed",
      description: "Natural FET",
      startDate: "2025-07-18",
      clinic: "Louise Fertility",
      outcome: { type: "miscarriage", notes: "Early loss." },
    },
    {
      id: "cycle-oi",
      cycleType: "oi",
      status: "closed",
      description: "Medications only",
      startDate: "2024-01-13",
      clinic: "Louise Fertility",
    },
    {
      id: "cycle-egg-freezing",
      cycleType: "egg_freezing",
      status: "closed",
      description: "Egg freezing",
      startDate: "2023-09-02",
      clinic: "Louise Fertility",
    },
    {
      id: "cycle-embryo-freezing",
      cycleType: "embryo_freezing",
      status: "closed",
      description: "Embryo freezing",
      startDate: "2023-11-21",
      clinic: "Louise Fertility",
    },
  ],
  obGynHistory: {
    id: "obgyn-canvas",
    hasBeenPregnant: true,
    hasContraceptionHistory: true,
    pregnancies: [
      {
        id: "pregnancy-canvas",
        startDate: "2021-03-01",
        outcome: "live_birth",
        deliveryDate: "2021-12-02",
        deliveryType: "c_section",
        withCurrentPartner: true,
        notes: "Gestational diabetes, resolved postpartum.",
      },
    ],
    contraception: [
      {
        id: "contraception-canvas",
        method: "hormonal_iud",
        contraceptionName: "Mirena",
        startDate: "2022-02-01",
        endDate: "2024-04-15",
        daysTillPeriodAfterStopping: 45,
        notes: "Removed before trying to conceive.",
      },
    ],
    menstrualHealth: {
      id: "menstrual-canvas",
      cycleStatus: "regular_cycle",
      ageAtFirstPeriod: 12,
      minAverageCycleLength: 27,
      maxAverageCycleLength: 31,
      menstrualFlowIntensity: "medium",
      period1StartDate: "2026-06-19",
      period1EndDate: "2026-06-24",
      period2StartDate: "2026-05-22",
      period2EndDate: "2026-05-27",
      period3StartDate: "2026-04-24",
      period3EndDate: "2026-04-29",
      bleedsOutsidePeriod: false,
      painScale: 6,
      takesPainMedication: true,
      hasPeriodSymptoms: true,
      awareOfFertileWindow: true,
      symptoms: [
        { id: "symptom-cramping", symptom: "cramping" },
        { id: "symptom-other", symptom: "other", other: "Lower back pain." },
      ],
    },
    sexualHealth: {
      id: "sexual-canvas",
      hadPapSmear: true,
      papSmearResultAbnormal: true,
      hadPapSmearProcedures: true,
      papSmearProcedures: [
        {
          id: "pap-procedure-canvas",
          procedure: "colposcopy",
          procedureDate: "2024-05-05",
          notes: "Follow-up was normal.",
        },
      ],
      hadSTDs: true,
      stds: [
        {
          id: "std-canvas",
          stdType: "hpv",
          dateOfDiagnosis: "2022-09-12",
          treatment: "Monitoring",
          notes: "Cleared on repeat testing.",
        },
      ],
      hasVaginalInfectionHistory: true,
      vaginalInfectionHistories: [
        {
          id: "infection-canvas",
          vaginalInfectionHistory: "past",
          description: "Yeast infection after antibiotics.",
          startDate: "2024-12-01",
          notes: "Resolved with treatment.",
        },
      ],
      hasPainfulIntercourse: true,
      painfulIntercourseScale: 4,
      usesLubricant: true,
      lubricantName: "Water-based lubricant",
      lubricantUseFrequency: "sometimes",
      isPartnerEjaculatingInVagina: true,
      vaginalEjaculationFrequency: "often",
    },
  },
  maleHistory: {
    id: "male-canvas",
    hasContraceptionHistory: true,
    contraceptions: [
      {
        id: "male-contraception-canvas",
        method: "male_condom",
        contraceptionName: "Condoms",
        startDate: "2025-01-01",
        notes: "Intermittent use.",
      },
    ],
    sexualHealth: {
      id: "male-sexual-canvas",
      hasDifficultiesWithErection: true,
    },
  },
  medicalHistory: {
    id: "medical-history-canvas",
    hasEndometriosis: "yes_diagnosed",
    suspectPCOS: true,
    hadVaricoceles: "yes_undiagnosed",
    hasAllergies: true,
    allergies: [
      {
        id: "allergy-canvas",
        allergy: "medications",
        name: "Penicillin",
        comments: "Hives as a child.",
        isUnderTreatment: false,
      },
    ],
    currentlyTakingMedications: true,
    medications: [
      {
        id: "med-canvas",
        name: "Cetirizine",
        brand: "Zyrtec",
        frequency: "Daily",
        notes: "Seasonal use.",
      },
      {
        id: "med-other-canvas",
        name: "Prenatal vitamin",
        frequency: "Daily",
        notes: "Contains folic acid.",
      },
    ],
    vaccinations: [
      {
        id: "vaccine-canvas",
        type: "rubella",
        brand: "MMR",
        dateReceived: "2020-01-15",
        notes: "Immune on titers.",
      },
    ],
    hasSurgeries: true,
    surgeries: [
      {
        id: "surgery-canvas",
        surgery: "operative_laparoscopy",
        date: "2020-05-20",
        comments: "Endometriosis excision.",
      },
    ],
    hasBreastTestings: true,
    hasBreastConditions: true,
    breastConditions: [
      {
        id: "breast-condition-canvas",
        condition: "other",
        other: "Dense breast tissue.",
      },
    ],
    hasCancerHistory: true,
    cancers: [
      {
        id: "cancer-canvas",
        cancer: "thyroid cancer",
        diagnosisDate: "2016-04-12",
        inRemission: "yes",
        treatments: [
          {
            id: "cancer-treatment-canvas",
            treatmentType: "surgery",
            startDate: "2016-05-01",
            endDate: "2016-05-01",
            notes: "Partial thyroidectomy.",
          },
        ],
      },
    ],
    hasEndocrineDiseases: true,
    hasInfectiousDiseases: false,
    hasCardiovascularDiseases: false,
    hasRespiratoryDiseases: true,
    hasGastrointestinalDiseases: false,
    hasHepaticBiliaryDiseases: false,
    hasRenalUrinaryDiseases: false,
    hasMusculoskeletalDiseases: false,
    hasHaematologicalDiseases: false,
    hasAutoimmuneDiseases: false,
    hasGeneticDiseases: false,
    hasEatingDisorders: false,
    hasNeurologicalMentalHealthDiseases: true,
    diseases: [
      {
        id: "disease-pcos",
        category: "endocrine",
        disease: "pcos",
        diagnosisDate: "2019-07-09",
        treatment: "Metformin previously.",
      },
      {
        id: "disease-asthma",
        category: "respiratory",
        disease: "asthma",
        diagnosisDate: "2001-03-01",
        treatment: "Rescue inhaler.",
      },
      {
        id: "disease-anxiety",
        category: "neurological_mental_health",
        disease: "anxiety",
        diagnosisDate: "2018-09-01",
        treatment: "Therapy.",
      },
    ],
  },
  lifestyle: {
    id: "lifestyle-canvas",
    hasExposures: true,
    exposures: [
      {
        id: "exposure-canvas",
        exposure: "chemicals",
        frequency: "1_a_week",
        context: "Works in a lab with solvent exposure.",
      },
    ],
    hasAddictions: true,
    addictions: [
      {
        id: "substance-canvas",
        substance: "alcohol",
        frequency: "monthly_or_less",
        averageQuantity: 2,
        startDate: "2010-01-01",
        notes: "Social use only.",
      },
    ],
    minHoursOfSleep: 6,
    maxHoursOfSleep: 8,
    isExercising: true,
    minHoursOfExercise: 2,
    maxHoursOfExercise: 4,
    hasDiets: true,
    diets: [
      {
        id: "diet-canvas",
        diet: "vegetarian",
      },
    ],
    hasDietSupplements: true,
    dietSupplements: [
      {
        id: "supplement-canvas",
        supplement: "folic_acid",
        startDate: "2025-01-01",
        frequency: "at_least_5_times_week",
        notes: "800 mcg daily.",
      },
    ],
  },
  familyHistory: {
    id: "family-canvas",
    ancestryHistory: [
      { id: "ancestry-canvas", ancestry: "caucasian" },
      { id: "ancestry-2-canvas", ancestry: "asian" },
    ],
    hasDoneDNAAncestryReport: true,
    dnaAncestryDocumentId: "dna-report-canvas",
    dnaAncestryReportDate: "2024-03-15",
    adoptionStatus: "not_adopted",
    hasRelativeFacingFertilityIssues: "yes",
    familyMemberWithFertilityIssues: [
      { id: "family-member-canvas", familyMember: "sister" },
    ],
    hasMotherTakenDESDuringPregnancy: "unknown",
    diseasesMothersSide: "Breast cancer, thyroid disease.",
    diseasesFathersSide: "Hypertension.",
  },
};

const MOCK_PATIENT_AMAB: RuntimePatientFixture = {
  ...MOCK_PATIENT_AFAB,
  gender: "AMAB",
  obGynHistory: null,
  maleHistory: {
    id: "male-canvas",
    hasContraceptionHistory: true,
    contraceptions: [
      {
        id: "male-contraception-canvas",
        method: "male_condom",
        contraceptionName: "Condoms",
        startDate: "2025-01-01",
        notes: "Intermittent use.",
      },
    ],
    sexualHealth: {
      id: "male-sexual-canvas",
      hasDifficultiesWithErection: true,
    },
  },
};

const FLOWS: RuntimeFlow[] = [
  {
    id: "overview",
    category: "Medical History overview",
    screens: [
      {
        name: "MedicalHistoryOverview",
        label: "Overview and section cards",
        component: MedicalHistoryOverviewSurface,
      },
    ],
  },
  {
    id: "basic-info",
    category: "Basic Info",
    provider: BasicInfoProvider,
    screens: [
      {
        name: "SexAtBirth",
        label: "Sex at birth",
        component: SexAtBirthScreen,
      },
      {
        name: "BasicInfo",
        label: "Height, weight, birthday",
        component: BasicInfoScreen,
      },
      {
        name: "QuickExposure",
        label: "Current substance exposure",
        component: QuickExposureScreen,
      },
      {
        name: "Pregnancy",
        label: "Pregnancy status",
        component: BasicPregnancyScreen,
      },
      {
        name: "Endometriosis",
        label: "Endometriosis",
        component: EndometriosisScreen,
      },
      { name: "PCOS", label: "PCOS", component: PCOSScreen },
      {
        name: "Varicocele",
        label: "Varicocele",
        component: VaricoceleScreen,
        patientSex: "AMAB",
      },
      { name: "Cancer", label: "Cancer history", component: BasicCancerScreen },
      {
        name: "Allergies",
        label: "Medication allergies",
        component: BasicAllergiesScreen,
      },
      {
        name: "AllergiesDetails",
        label: "Medication allergy details",
        component: BasicAllergiesDetailsScreen,
      },
      {
        name: "Medications",
        label: "Current medication summary",
        component: BasicMedicationsScreen,
      },
    ],
  },
  {
    id: "infertility",
    category: "Infertility History",
    provider: InfertilityProvider,
    screens: [
      {
        name: "InfertilityDiagnosis",
        label: "Infertility diagnosis",
        component: InfertilityDiagnosisScreen,
      },
      {
        name: "InfertilityCause",
        label: "Infertility causes",
        component: InfertilityCauseScreen,
      },
      {
        name: "InfertilityCauseOther",
        label: "Other infertility cause",
        component: InfertilityCauseOtherScreen,
      },
      {
        name: "PartnerInfertilityDiagnosis",
        label: "Partner infertility diagnosis",
        component: PartnerInfertilityDiagnosisScreen,
      },
    ],
  },
  {
    id: "fertility-testing",
    category: "Fertility Testing",
    provider: FertilityTestingProvider,
    screens: [
      {
        name: "FertilityTestingTypes",
        label: "Testing types",
        component: FertilityTestingTypesScreen,
      },
      {
        name: "UrineTests",
        label: "Urine test details",
        component: UrineTestsScreen,
      },
      {
        name: "ImagingTests",
        label: "Imaging test details",
        component: ImagingTestsScreen,
      },
      {
        name: "SamplingTests",
        label: "Sampling test details",
        component: SamplingTestsScreen,
      },
      {
        name: "GeneticTests",
        label: "Genetic test details",
        component: GeneticTestsScreen,
      },
      {
        name: "BloodTests",
        label: "Blood test details",
        component: BloodTestsScreen,
      },
      {
        name: "FertilityTestingComplete",
        label: "Testing complete",
        component: FertilityTestingCompleteScreen,
      },
    ],
  },
  {
    id: "fertility-treatments",
    category: "Fertility Treatments",
    provider: FertilityTreatmentsProvider,
    screens: [
      {
        name: "FertilityTreatmentsTypes",
        label: "Past treatment types",
        component: FertilityTreatmentsTypesScreen,
      },
      {
        name: "IUITreatments",
        label: "IUI treatment details",
        component: IUITreatmentsScreen,
      },
      {
        name: "IVFTreatments",
        label: "IVF treatment details",
        component: IVFTreatmentsScreen,
      },
      {
        name: "MedsOnlyTreatments",
        label: "Medication-only treatment details",
        component: MedsOnlyTreatmentsScreen,
      },
      {
        name: "OtherTreatments",
        label: "Other treatment details",
        component: OtherTreatmentsScreen,
      },
      {
        name: "FETTreatments",
        label: "FET treatment details",
        component: FETTreatmentsScreen,
      },
      {
        name: "EggFreezing",
        label: "Egg freezing details",
        component: EggFreezingScreen,
      },
      {
        name: "EmbryoFreezing",
        label: "Embryo freezing details",
        component: EmbryoFreezingScreen,
      },
    ],
  },
  {
    id: "menstrual-health",
    category: "Menstrual Health",
    provider: MenstrualHealthProvider,
    screens: [
      {
        name: "CycleStatus",
        label: "Cycle status",
        component: CycleStatusScreen,
      },
      {
        name: "AgeAtFirstPeriod",
        label: "Age at first period",
        component: AgeAtFirstPeriodScreen,
      },
      {
        name: "Last3Periods",
        label: "Last three periods",
        component: Last3PeriodsScreen,
      },
      {
        name: "BleedOutsidePeriod",
        label: "Bleeding outside period",
        component: BleedOutsidePeriodScreen,
      },
      { name: "PainScale", label: "Pain scale", component: PainScaleScreen },
      {
        name: "PainMedication",
        label: "Pain medication",
        component: PainMedicationScreen,
      },
      { name: "Symptoms", label: "Period symptoms", component: SymptomsScreen },
      {
        name: "FertileWindow",
        label: "Fertile window awareness",
        component: FertileWindowScreen,
      },
    ],
  },
  {
    id: "sexual-health",
    category: "Sexual Health",
    provider: SexualHealthProvider,
    screens: [
      { name: "PapSmear", label: "Pap smear", component: PapSmearScreen },
      {
        name: "PapSmearProcedures",
        label: "Pap smear procedures",
        component: PapSmearProceduresScreen,
      },
      {
        name: "PapSmearProcedureDetails",
        label: "Pap smear procedure details",
        component: PapSmearProcedureDetailsScreen,
      },
      { name: "STDs", label: "STIs/STDs", component: STDsScreen },
      {
        name: "STDDetails",
        label: "STI/STD details",
        component: STDDetailsScreen,
      },
      {
        name: "VaginalInfections",
        label: "Vaginal infections",
        component: VaginalInfectionsScreen,
      },
      {
        name: "VaginalInfectionDetails",
        label: "Vaginal infection details",
        component: VaginalInfectionDetailsScreen,
      },
      {
        name: "PainfulIntercourse",
        label: "Painful intercourse",
        component: PainfulIntercourseScreen,
      },
      {
        name: "Lubricants",
        label: "Lubricants",
        component: LubricantsScreen,
        applicability: SEXUAL_HEALTH_QUESTION_APPLICABILITY.Lubricants,
      },
      {
        name: "PartnerEjaculate",
        label: "Partner ejaculation (AFAB wording)",
        component: PartnerEjaculateScreen,
        patientSex: "AFAB",
        applicability: SEXUAL_HEALTH_QUESTION_APPLICABILITY.PartnerEjaculate,
      },
      {
        name: "PartnerEjaculateAMAB",
        label: "Partner ejaculation (AMAB wording)",
        component: PartnerEjaculateScreen,
        patientSex: "AMAB",
        applicability: SEXUAL_HEALTH_QUESTION_APPLICABILITY.PartnerEjaculate,
      },
      {
        name: "ErectileDifficulties",
        label: "Erectile difficulties",
        component: ErectileDifficultiesScreen,
        patientSex: "AMAB",
      },
    ],
  },
  {
    id: "contraception",
    category: "Contraception",
    provider: ContraceptionProvider,
    screens: [
      {
        name: "ContraceptionMethods",
        label: "Contraception methods",
        component: ContraceptionMethodsScreen,
      },
      {
        name: "ContraceptionDetails",
        label: "Contraception details",
        component: ContraceptionDetailsScreen,
      },
    ],
  },
  {
    id: "pregnancies",
    category: "Pregnancies",
    provider: PregnancyProvider,
    screens: [
      {
        name: "PregnancyUsage",
        label: "Pregnancy history",
        component: PregnancyUsageScreen,
      },
      {
        name: "PregnancyDetails",
        label: "Pregnancy details",
        component: PregnancyDetailsScreen,
      },
    ],
  },
  {
    id: "medications-vaccines",
    category: "Medications and Vaccines",
    provider: MedicationsVaccinesProvider,
    screens: [
      {
        name: "MedicationsIntro",
        label: "Medication intro",
        component: MedicationsIntroScreen,
      },
      {
        name: "MedicationsProducts",
        label: "Medication products",
        component: MedicationsProductsScreen,
      },
      {
        name: "MedicationDetails",
        label: "Medication details",
        component: MedicationDetailsScreen,
      },
      {
        name: "VaccinesSelect",
        label: "Vaccines",
        component: VaccinesSelectScreen,
      },
      {
        name: "VaccineDetails",
        label: "Vaccine details",
        component: VaccineDetailsScreen,
      },
    ],
  },
  {
    id: "allergies",
    category: "Allergies",
    provider: AllergiesProvider,
    screens: [
      {
        name: "AllergiesIntro",
        label: "Medication allergy intro",
        component: AllergiesIntroScreen,
      },
      {
        name: "AllergiesDetails",
        label: "Medication allergy details",
        component: AllergiesDetailsScreen,
      },
    ],
  },
  {
    id: "surgeries",
    category: "Surgeries",
    provider: SurgeriesProvider,
    screens: [
      {
        name: "SurgeriesIntro",
        label: "Surgery history",
        component: SurgeriesScreen,
      },
      {
        name: "SurgeriesDetails",
        label: "Surgery details",
        component: SurgeriesDetailsScreen,
      },
    ],
  },
  {
    id: "disease-history",
    category: "Disease History",
    provider: DiseaseHistoryProvider,
    screens: [
      {
        name: "BreastTesting",
        label: "Breast testing",
        component: BreastTestingScreen,
      },
      {
        name: "BreastTestingDetails",
        label: "Breast testing details",
        component: BreastTestingDetailsScreen,
      },
      {
        name: "BreastConditions",
        label: "Breast conditions",
        component: BreastConditionsScreen,
      },
      {
        name: "BreastSymptomOther",
        label: "Other breast symptom",
        component: BreastSymptomOtherScreen,
      },
      ...DISEASE_CATEGORY_OPTIONS.map((category) => ({
        name: `Disease_${category.id}`,
        label: `Disease category: ${category.id.replace(/_/g, " ")}`,
        component: DiseaseSelectionScreen,
      })),
      {
        name: "DiseaseDetails",
        label: "Disease details",
        component: DiseaseDetailsScreen,
      },
      {
        name: "CancerHistory",
        label: "Cancer history",
        component: CancerHistoryScreen,
      },
      {
        name: "CancerDetails",
        label: "Cancer details",
        component: CancerDetailsScreen,
      },
      {
        name: "CancerTreatments",
        label: "Cancer treatments",
        component: CancerTreatmentsScreen,
      },
      {
        name: "CancerTreatmentDetails",
        label: "Cancer treatment details",
        component: CancerTreatmentDetailsScreen,
      },
    ],
  },
  {
    id: "exposure",
    category: "Exposure",
    provider: ExposureProvider,
    suspense: true,
    screens: [
      {
        name: "ExposureTypes",
        label: "Exposure types",
        component: ExposureTypesScreen,
      },
      {
        name: "ExposureDetails",
        label: "Exposure details",
        component: ExposureDetailsScreen,
        initialParams: { exposure: "chemicals" },
      },
    ],
  },
  {
    id: "substances",
    category: "Substances",
    provider: SubstancesProvider,
    screens: [
      {
        name: "SubstancesIntro",
        label: "Substances intro",
        component: SubstancesIntroScreen,
      },
      {
        name: "SubstancesSelect",
        label: "Substance selection",
        component: SubstancesSelectScreen,
      },
      {
        name: "SubstanceDetails",
        label: "Substance details",
        component: SubstanceDetailsScreen,
        initialParams: { substance: "alcohol" },
      },
    ],
  },
  {
    id: "sleep-activity",
    category: "Sleep and Activity",
    provider: SleepActivityProvider,
    screens: [
      { name: "SleepIntro", label: "Sleep", component: SleepScreen },
      { name: "ActivityIntro", label: "Activity", component: ActivityScreen },
    ],
  },
  {
    id: "diet-supplements",
    category: "Diet and Supplements",
    provider: DietSupplementsProvider,
    screens: [
      { name: "DietIntro", label: "Diet", component: DietScreen },
      {
        name: "SupplementsIntro",
        label: "Supplements",
        component: SupplementsScreen,
      },
      {
        name: "SupplementsDetails",
        label: "Supplement details",
        component: SupplementsDetailsScreen,
      },
    ],
  },
  {
    id: "family-history",
    category: "Family History",
    provider: FamilyHistoryProvider,
    screens: [
      {
        name: "Ethnicity",
        label: "Ethnicity and ancestry",
        component: EthnicityScreen,
      },
      {
        name: "DnaTest",
        label: "DNA ancestry report",
        component: DnaTestScreen,
      },
      {
        name: "DnaTestReport",
        label: "DNA report details",
        component: DnaTestReportScreen,
      },
      { name: "Adopted", label: "Adoption status", component: AdoptedScreen },
      {
        name: "FamilyInfertility",
        label: "Family infertility",
        component: FamilyInfertilityScreen,
      },
      {
        name: "FamilyMember",
        label: "Family member details",
        component: FamilyMemberScreen,
      },
      { name: "Des", label: "DES exposure", component: DesScreen },
      {
        name: "MaternalDiseases",
        label: "Maternal family diseases",
        component: MaternalDiseasesScreen,
      },
      {
        name: "PaternalDiseases",
        label: "Paternal family diseases",
        component: PaternalDiseasesScreen,
      },
    ],
  },
];

const TARGETS: RuntimeTarget[] = FLOWS.flatMap((flow) =>
  flow.screens.map((screen) => ({
    ...screen,
    id: `${flow.id}-${slugify(screen.name)}`,
    category: flow.category,
    flow,
  })),
);

const FLOW_QUESTION_METADATA_KEYS: Record<
  string,
  { sectionLabelKey: string; helpTextKey: string }
> = {
  "basic-info": {
    sectionLabelKey: "med_history.review.section_title.basic-info",
    helpTextKey: "med_history.question_screen.flow_help.basic-info",
  },
  infertility: {
    sectionLabelKey: "med_history.review.section_title.infertility",
    helpTextKey: "med_history.question_screen.flow_help.infertility",
  },
  "fertility-testing": {
    sectionLabelKey: "med_history.review.section_title.fertility-testing",
    helpTextKey: "med_history.question_screen.flow_help.fertility-testing",
  },
  "fertility-treatments": {
    sectionLabelKey: "med_history.review.section_title.fertility-treatments",
    helpTextKey: "med_history.question_screen.flow_help.fertility-treatments",
  },
  "menstrual-health": {
    sectionLabelKey: "med_history.review.section_title.menstrual-health",
    helpTextKey: "med_history.question_screen.flow_help.menstrual-health",
  },
  "sexual-health": {
    sectionLabelKey: "med_history.review.section_title.sexual-health",
    helpTextKey: "med_history.question_screen.flow_help.sexual-health",
  },
  contraception: {
    sectionLabelKey: "med_history.review.section_title.contraception",
    helpTextKey: "med_history.question_screen.flow_help.contraception",
  },
  pregnancies: {
    sectionLabelKey: "med_history.review.section_title.pregnancies",
    helpTextKey: "med_history.question_screen.flow_help.pregnancies",
  },
  "medications-vaccines": {
    sectionLabelKey: "med_history.review.section_title.medications-vaccines",
    helpTextKey: "med_history.question_screen.flow_help.medications-vaccines",
  },
  allergies: {
    sectionLabelKey: "med_history.review.section_title.allergies",
    helpTextKey: "med_history.question_screen.flow_help.allergies",
  },
  "disease-history": {
    sectionLabelKey: "med_history.review.section_title.diseases",
    helpTextKey: "med_history.question_screen.flow_help.diseases",
  },
  surgeries: {
    sectionLabelKey: "med_history.review.section_title.surgeries",
    helpTextKey: "med_history.question_screen.flow_help.surgeries",
  },
  exposure: {
    sectionLabelKey: "med_history.review.section_title.exposure",
    helpTextKey: "med_history.question_screen.flow_help.exposure",
  },
  substances: {
    sectionLabelKey: "med_history.review.section_title.substances",
    helpTextKey: "med_history.question_screen.flow_help.substances",
  },
  "sleep-activity": {
    sectionLabelKey: "med_history.review.section_title.sleep-activity",
    helpTextKey: "med_history.question_screen.flow_help.sleep-activity",
  },
  "diet-supplements": {
    sectionLabelKey: "med_history.review.section_title.diet-supplements",
    helpTextKey: "med_history.question_screen.flow_help.diet-supplements",
  },
  "family-history": {
    sectionLabelKey: "med_history.review.section_title.family-history",
    helpTextKey: "med_history.question_screen.flow_help.family-history",
  },
};

const FIRST_TARGET: RuntimeTarget = (() => {
  const t = TARGETS[0];
  if (!t) throw new Error("TARGETS must not be empty");
  return t;
})();

function RuntimeFlowRenderer({
  target,
  onClose,
}: {
  target: RuntimeTarget;
  onClose: () => void;
}) {
  const Provider = target.flow.provider ?? Fragment;
  const questionMetadata = FLOW_QUESTION_METADATA_KEYS[target.flow.id];
  const stack = (
    <Provider>
      <MedicalHistoryQuestionProvider
        sectionLabelKey={questionMetadata?.sectionLabelKey}
        helpTextKey={questionMetadata?.helpTextKey}
        onClose={onClose}
      >
        <DelayedRuntimeMount mountKey={target.id}>
          <RuntimeStack.Navigator
            key={target.id}
            initialRouteName={target.name}
            screenOptions={{
              headerShown: false,
              contentStyle: { backgroundColor: "transparent" },
            }}
          >
            {target.flow.screens.map((screen) => {
              const ScreenComponent = screen.component;
              return (
                <RuntimeStack.Screen
                  key={screen.name}
                  name={screen.name}
                  initialParams={screen.initialParams}
                >
                  {() => <ScreenComponent />}
                </RuntimeStack.Screen>
              );
            })}
          </RuntimeStack.Navigator>
        </DelayedRuntimeMount>
      </MedicalHistoryQuestionProvider>
    </Provider>
  );

  if (target.flow.suspense) {
    return (
      <Suspense fallback={<View style={styles.loadingSurface} />}>
        {stack}
      </Suspense>
    );
  }

  return stack;
}

function DelayedRuntimeMount({
  children,
  mountKey,
}: {
  children: ReactNode;
  mountKey: string;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
    const timeout = setTimeout(() => setReady(true), 150);
    return () => clearTimeout(timeout);
  }, [mountKey]);

  if (!ready) {
    return <View style={styles.loadingSurface} />;
  }

  return <>{children}</>;
}

function getPatientForTarget(target: RuntimeTarget): RuntimePatientFixture {
  const patient =
    target.patientSex === "AMAB" ? MOCK_PATIENT_AMAB : MOCK_PATIENT_AFAB;

  // Keep this target unanswered so the canvas exercises the same first-time
  // interaction as a patient who has not yet provided sex at birth.
  if (target.name === "SexAtBirth") {
    return { ...patient, gender: undefined };
  }

  // Pregnancy has a conditional count step. Keep the gateway unanswered so
  // the canvas can verify that the no branch skips that step entirely.
  if (target.name === "Pregnancy") {
    const obGynHistory = patient.obGynHistory;
    return {
      ...patient,
      obGynHistory:
        obGynHistory && typeof obGynHistory === "object"
          ? {
              ...(obGynHistory as Record<string, unknown>),
              hasBeenPregnant: null,
              pregnancies: [],
            }
          : { hasBeenPregnant: null, pregnancies: [] },
    };
  }

  const menstrualFieldByTarget: Record<string, string> = {
    CycleStatus: "cycleStatus",
    BleedOutsidePeriod: "bleedsOutsidePeriod",
    PainMedication: "takesPainMedication",
    FertileWindow: "awareOfFertileWindow",
  };
  const menstrualField = menstrualFieldByTarget[target.name];
  if (menstrualField) {
    const obGynHistory = (patient.obGynHistory ?? {}) as Record<
      string,
      unknown
    >;
    const menstrualHealth = (obGynHistory.menstrualHealth ?? {}) as Record<
      string,
      unknown
    >;
    return {
      ...patient,
      obGynHistory: {
        ...obGynHistory,
        menstrualHealth: {
          ...menstrualHealth,
          [menstrualField]: null,
        },
      },
    };
  }

  if (
    [
      "Endometriosis",
      "PCOS",
      "Varicocele",
      "Cancer",
      "Allergies",
      "Medications",
    ].includes(target.name)
  ) {
    const medicalHistory = (patient.medicalHistory ??
      {}) as RuntimeMedicalHistoryFixture;
    return {
      ...patient,
      medicalHistory: {
        ...medicalHistory,
        ...(target.name === "Endometriosis" ? { hasEndometriosis: null } : {}),
        ...(target.name === "PCOS"
          ? {
              suspectPCOS: null,
              diseases: (medicalHistory.diseases ?? []).filter(
                (disease) => disease.disease !== "pcos",
              ),
            }
          : {}),
        ...(target.name === "Varicocele" ? { hadVaricoceles: null } : {}),
        ...(target.name === "Cancer" ? { hasCancerHistory: null } : {}),
        ...(target.name === "Allergies" ? { hasAllergies: null } : {}),
        ...(target.name === "Medications"
          ? { currentlyTakingMedications: null }
          : {}),
      },
    };
  }

  return patient;
}

export default function MedicalHistoryCanvasScreen() {
  const queryClient = useQueryClient();
  const capturePress = usePressCapture("MedicalHistoryCanvasScreen");
  const [selectedId, setSelectedId] = useState(FIRST_TARGET.id);
  const selectedTarget = useMemo(
    () => TARGETS.find((target) => target.id === selectedId) ?? FIRST_TARGET,
    [selectedId],
  );
  const [seededTargetId, setSeededTargetId] = useState<string | null>(null);

  useEffect(() => {
    const patient = getPatientForTarget(selectedTarget);
    queryClient.setQueryData(["patient", "olly", undefined], patient);
    setSeededTargetId(selectedTarget.id);
  }, [queryClient, selectedTarget]);

  const groupedTargets = useMemo(() => {
    return FLOWS.map((flow) => ({
      category: flow.category,
      targets: TARGETS.filter((target) => target.flow.id === flow.id),
    }));
  }, []);

  const seeded = seededTargetId === selectedTarget.id;

  return (
    <View style={styles.root}>
      <View style={styles.sidebar} testID="medical-history-canvas-nav">
        <Text style={styles.eyebrow}>Runtime audit canvas</Text>
        <Text style={styles.title} testID="medical-history-canvas-title">
          Medical History
        </Text>
        <Text style={styles.description}>
          Real app screens mounted with their flow providers and a seeded
          patient fixture.
        </Text>
        <ScrollView
          style={styles.navScroll}
          contentContainerStyle={styles.navContent}
          showsVerticalScrollIndicator={false}
        >
          {groupedTargets.map((group) => (
            <View key={group.category} style={styles.navGroup}>
              <Text style={styles.groupTitle}>{group.category}</Text>
              {group.targets.map((target) => {
                const isActive = target.id === selectedTarget.id;
                return (
                  <Pressable
                    key={target.id}
                    testID={`medical-history-canvas-surface-${target.id}`}
                    accessibilityRole="button"
                    accessibilityLabel={`${target.category}: ${target.label}`}
                    onPress={() => {
                      capturePress(
                        pressLabel(`${target.category}: ${target.label}`),
                        `canvas_surface_${target.id}`,
                      );
                      setSelectedId(target.id);
                    }}
                    style={[
                      styles.navButton,
                      isActive && styles.navButtonActive,
                    ]}
                  >
                    <Text
                      style={[
                        styles.navButtonCategory,
                        isActive && styles.navButtonTextActive,
                      ]}
                    >
                      {target.category}
                    </Text>
                    <Text
                      style={[
                        styles.navButtonLabel,
                        isActive && styles.navButtonTextActive,
                      ]}
                    >
                      {target.label}
                    </Text>
                    {target.applicability ? (
                      <>
                        <Text style={styles.navButtonApplicability}>
                          Conditional: {target.applicability.condition}
                        </Text>
                        {target.applicability.wording ? (
                          <Text style={styles.navButtonApplicability}>
                            Wording: {target.applicability.wording}
                          </Text>
                        ) : null}
                      </>
                    ) : null}
                  </Pressable>
                );
              })}
            </View>
          ))}
        </ScrollView>
      </View>

      <View style={styles.workspace}>
        <View style={styles.workspaceHeader}>
          <Text style={styles.selectedCategory}>{selectedTarget.category}</Text>
          <Text
            style={styles.selectedLabel}
            testID="medical-history-canvas-selected-label"
          >
            {selectedTarget.label}
          </Text>
        </View>
        <View testID="medical-history-canvas-content" style={styles.phoneFrame}>
          <MedicalHistoryThemeProvider theme={MOON_THEME}>
            {seeded ? (
              <RuntimeFlowRenderer
                key={selectedTarget.id}
                target={selectedTarget}
                onClose={() => setSelectedId("overview")}
              />
            ) : (
              <View style={styles.loadingSurface} />
            )}
          </MedicalHistoryThemeProvider>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: "#F4EEE7",
  },
  sidebar: {
    width: 360,
    borderRightWidth: 1,
    borderRightColor: "rgba(42, 37, 34, 0.12)",
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 16,
    backgroundColor: "#FFF9F2",
  },
  eyebrow: {
    color: "#8A7D74",
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  title: {
    color: "#2A2522",
    fontSize: 28,
    lineHeight: 34,
    fontWeight: "700",
    marginTop: 6,
  },
  description: {
    color: "#5A4D45",
    fontSize: 13,
    lineHeight: 18,
    marginTop: 8,
  },
  navScroll: {
    flex: 1,
    marginTop: 20,
  },
  navContent: {
    paddingBottom: 24,
  },
  navGroup: {
    marginBottom: 18,
  },
  groupTitle: {
    color: "#8A7D74",
    fontSize: 12,
    fontWeight: "700",
    marginBottom: 8,
  },
  navButton: {
    minHeight: 56,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(184, 74, 22, 0.12)",
    backgroundColor: "rgba(255, 255, 255, 0.56)",
    marginBottom: 8,
  },
  navButtonActive: {
    borderColor: "rgba(184, 74, 22, 0.42)",
    backgroundColor: "rgba(184, 74, 22, 0.08)",
  },
  navButtonCategory: {
    color: "#8A7D74",
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "600",
  },
  navButtonLabel: {
    color: "#2A2522",
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "600",
    marginTop: 2,
  },
  navButtonApplicability: {
    color: "#8A7D74",
    fontSize: 10,
    lineHeight: 13,
    marginTop: 5,
  },
  navButtonTextActive: {
    color: "#B84A16",
  },
  workspace: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 24,
    paddingHorizontal: 32,
  },
  workspaceHeader: {
    width: 430,
    marginBottom: 12,
  },
  selectedCategory: {
    color: "#8A7D74",
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "600",
  },
  selectedLabel: {
    color: "#2A2522",
    fontSize: 22,
    lineHeight: 28,
    fontWeight: "700",
    marginTop: 2,
  },
  phoneFrame: {
    width: 430,
    height: 900,
    overflow: "hidden",
    borderRadius: 28,
    borderWidth: 1,
    borderColor: "rgba(42, 37, 34, 0.16)",
    backgroundColor: "#FAF6F2",
    shadowColor: "#2A2522",
    shadowOffset: { width: 0, height: 18 },
    shadowOpacity: 0.16,
    shadowRadius: 28,
  },
  loadingSurface: {
    flex: 1,
    backgroundColor: "#FAF6F2",
  },
});
